/**
 * Day 2, the reviewable Business Profile draft: every evidence-backed field
 * as an Owner-editable control, the conflicts and unknowns, the Offers, and
 * the correction, approval and rejection actions while the run awaits
 * review.
 */

import type { ReactNode } from 'react';

import type {
  BusinessProfileDraft,
  BusinessProfileOfferDraft,
  EvidenceBackedProfileField,
} from '@mouja/domain/analysis';

import {
  approveAnalysisProfile,
  correctAnalysisProfile,
  newIdempotencyKey,
  rejectAnalysisProfile,
} from '../../api.js';
import { Button, Tag, TextAreaField, TextField } from '../../ui.js';
import { SectionHeading } from '../common.js';
import type { AnalysisRunPageCopy } from './run-page-copy.js';
import { card } from './run-page-styles.js';
import type { RunMutation } from './useAnalysisRunMutation.js';
import type { useBusinessProfileDraft } from './useBusinessProfileDraft.js';

function ownerField(
  value: string | null,
  prior?: EvidenceBackedProfileField,
): EvidenceBackedProfileField {
  return {
    value,
    basis: 'OWNER_CONFIRMED',
    confidence: null,
    evidenceIds: prior?.evidenceIds ?? [],
    reviewState: value === null || value.trim() === '' ? 'REMOVED' : 'OWNER_CONFIRMED',
  };
}

function blankOffer(index: number): BusinessProfileOfferDraft {
  const empty = ownerField(null);
  return {
    localId: `owner-offer-${crypto.randomUUID()}-${index}`,
    title: empty,
    category: ownerField('SERVICE'),
    description: empty,
    audienceOrNeed: empty,
    deliveryMode: empty,
    geographyOrAccess: empty,
    price: empty,
    proof: [],
    conversionPath: empty,
  };
}

function FieldEditor({
  label,
  field,
  onChange,
  multiline = false,
}: {
  readonly label: string;
  readonly field: EvidenceBackedProfileField;
  readonly onChange: (field: EvidenceBackedProfileField) => void;
  readonly multiline?: boolean;
}): ReactNode {
  return (
    <div style={{ ...card, background: 'var(--surface-subtle)' }}>
      {multiline ? (
        <TextAreaField
          label={label}
          value={field.value ?? ''}
          onChange={(value) => {
            onChange(ownerField(value, field));
          }}
          rows={3}
          maxLength={4000}
        />
      ) : (
        <TextField
          label={label}
          value={field.value ?? ''}
          onChange={(value) => {
            onChange(ownerField(value, field));
          }}
          maxLength={4000}
        />
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Tag tone={field.basis === 'UNKNOWN' ? 'warning' : 'info'}>{field.basis}</Tag>
        <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>
          {field.evidenceIds.length} evidence link(s)
        </span>
        <Button
          size="sm"
          variant="quiet"
          onClick={() => {
            onChange(ownerField(null, field));
          }}
        >
          Clear / unknown
        </Button>
      </div>
    </div>
  );
}

export function BusinessProfileEditor({
  id,
  profile,
  draft,
  currentDraftId,
  mayReview,
  busy,
  mutate,
  copy,
}: {
  readonly id: string;
  readonly profile: BusinessProfileDraft;
  readonly draft: ReturnType<typeof useBusinessProfileDraft>;
  readonly currentDraftId: string | null;
  readonly mayReview: boolean;
  readonly busy: string | null;
  readonly mutate: RunMutation;
  readonly copy: AnalysisRunPageCopy;
}): ReactNode {
  const {
    setProfile,
    correctionReason,
    setCorrectionReason,
    rejectionReason,
    setRejectionReason,
  } = draft;

  const patchSingular = (
    key: 'identity' | 'niche' | 'geography' | 'positioning',
    value: EvidenceBackedProfileField,
  ) => {
    setProfile((current) => (current === null ? null : { ...current, [key]: value }));
  };
  const patchOffer = (
    localId: string,
    key: keyof Omit<BusinessProfileOfferDraft, 'localId' | 'proof'>,
    value: EvidenceBackedProfileField,
  ) => {
    setProfile((current) =>
      current === null
        ? null
        : {
            ...current,
            offers: current.offers.map((offer) =>
              offer.localId === localId ? { ...offer, [key]: value } : offer,
            ),
          },
    );
  };

  return (
    <section id="business-profile" style={{ marginBottom: 'var(--space-lg)' }}>
      <SectionHeading>{copy.profile}</SectionHeading>
      <div className="mj-form-two-column">
        <FieldEditor
          label="Identity / name"
          field={profile.identity}
          onChange={(field) => {
            patchSingular('identity', field);
          }}
        />
        <FieldEditor
          label="Niche / category"
          field={profile.niche}
          onChange={(field) => {
            patchSingular('niche', field);
          }}
        />
        <FieldEditor
          label="Geography"
          field={profile.geography}
          onChange={(field) => {
            patchSingular('geography', field);
          }}
        />
        <FieldEditor
          label="Positioning"
          field={profile.positioning}
          multiline
          onChange={(field) => {
            patchSingular('positioning', field);
          }}
        />
      </div>

      {profile.conflicts.length > 0 ? (
        <div
          style={{
            ...card,
            borderColor: 'var(--status-warning-border)',
            marginTop: 12,
          }}
        >
          <strong>{copy.conflicts}</strong>
          <ul>
            {profile.conflicts.map((conflict) => (
              <li key={`${conflict.type}:${conflict.values.join('|')}`}>
                {conflict.type}: {conflict.values.join(' ↔ ')}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div style={{ ...card, marginTop: 12 }}>
        <strong>{copy.unknowns}</strong>
        <p>{profile.constraintsAndUnknowns.join(', ') || 'None'}</p>
      </div>

      <SectionHeading>{copy.offers}</SectionHeading>
      <div style={{ display: 'grid', gap: 12 }}>
        {profile.offers.map((offer, index) => (
          <article key={offer.localId} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <h3 style={{ font: 'var(--type-heading-sm)' }}>Offer {index + 1}</h3>
              <Button
                size="sm"
                variant="quiet"
                onClick={() => {
                  setProfile((current) =>
                    current === null
                      ? null
                      : {
                          ...current,
                          offers: current.offers.filter(
                            (item) => item.localId !== offer.localId,
                          ),
                        },
                  );
                }}
              >
                Remove
              </Button>
            </div>
            <div className="mj-form-two-column">
              {(
                [
                  ['title', 'Title'],
                  ['category', 'Category'],
                  ['description', 'Description'],
                  ['audienceOrNeed', 'Audience / need'],
                  ['deliveryMode', 'Delivery mode'],
                  ['geographyOrAccess', 'Geography / access'],
                  ['price', 'Public price'],
                  ['conversionPath', 'Conversion path'],
                ] as const
              ).map(([key, label]) => (
                <FieldEditor
                  key={key}
                  label={label}
                  field={offer[key]}
                  multiline={key === 'description'}
                  onChange={(field) => {
                    patchOffer(offer.localId, key, field);
                  }}
                />
              ))}
            </div>
          </article>
        ))}
      </div>
      <Button
        variant="outline"
        onClick={() => {
          setProfile((current) =>
            current === null
              ? null
              : {
                  ...current,
                  offers: [...current.offers, blankOffer(current.offers.length)],
                },
          );
        }}
        style={{ marginTop: 12 }}
      >
        {copy.addOffer}
      </Button>

      {mayReview && currentDraftId ? (
        <div style={{ ...card, marginTop: 16 }}>
          <TextAreaField
            label="Correction note (optional)"
            value={correctionReason}
            onChange={setCorrectionReason}
            rows={2}
            maxLength={4000}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              variant="outline"
              busy={busy === 'correct'}
              onClick={() =>
                void mutate('correct', async () => {
                  await correctAnalysisProfile(id, newIdempotencyKey(), {
                    expectedArtifactId: currentDraftId,
                    profile,
                    ...(correctionReason.trim() ? { reason: correctionReason } : {}),
                  });
                  setCorrectionReason('');
                })
              }
            >
              {copy.saveCorrection}
            </Button>
            <Button
              variant="primary"
              busy={busy === 'approve'}
              onClick={() =>
                void mutate('approve', () =>
                  approveAnalysisProfile(id, newIdempotencyKey(), currentDraftId),
                )
              }
            >
              {copy.approve}
            </Button>
          </div>
          <TextAreaField
            label="Rejection reason"
            value={rejectionReason}
            onChange={setRejectionReason}
            rows={2}
            maxLength={4000}
          />
          <Button
            variant="danger"
            busy={busy === 'reject'}
            disabled={!rejectionReason.trim()}
            onClick={() =>
              void mutate('reject', async () => {
                await rejectAnalysisProfile(id, newIdempotencyKey(), {
                  expectedArtifactId: currentDraftId,
                  reason: rejectionReason,
                });
                setRejectionReason('');
              })
            }
          >
            {copy.reject}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
